import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useInvoiceNavigation } from '@features/invoices/hooks/use-invoice-navigation/useInvoiceNavigation';
import * as React from 'react';

import type { InvoiceAlertPanelProps } from './InvoiceAlertPanel.types';

import { InvoiceAlertItem } from './invoice-alert-item/InvoiceAlertItem';
import styles from './InvoiceAlertPanel.module.css';

const InvoiceAlertPanel = React.memo(
  ({
    alert,
    isOpen = false,
    onClose,
  }: Readonly<InvoiceAlertPanelProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const isPastDue = alert?.priorityType === 'PastDue';
    const { navigateToInvoice } = useInvoiceNavigation();

    const descriptionSuffix = React.useMemo(
      () => (isPastDue ? 'past due.' : 'due in the next 7 days.'),
      [isPastDue]
    );

    return (
      <div className={isMobileOrTablet ? undefined : styles.panelWrapper}>
        <CobaltSidePanel
          size={isMobileOrTablet ? 'large' : 'small'}
          isOpen={isOpen}
          mode={isMobileOrTablet ? 'over' : 'push'}
          showCloseButton
          onClose={onClose}
          title={'Your alerts'}
          description={
            alert ? (
              <div className="body-02">
                You have{' '}
                <span className={styles.description}>
                  {alert.numberOfInvoices}{' '}
                  {alert.numberOfInvoices === 1 ? 'invoice' : 'invoices'} (
                  {alert.currency})
                </span>{' '}
                {descriptionSuffix}
              </div>
            ) : undefined
          }
          data-testid="invoice-alert-panel"
          className={isMobileOrTablet ? undefined : styles.alertPanelContainer}
        >
          <ul
            aria-label="Invoice alerts"
            className={styles.invoiceList}
            data-testid="invoice-alert-panel-list"
          >
            {alert.invoices.map(invoice => (
              <li
                key={invoice.accountReceivableId}
                className={styles.invoiceListItem}
              >
                <InvoiceAlertItem
                  invoice={invoice}
                  isPastDue={isPastDue}
                  onInvoiceClick={navigateToInvoice}
                />
              </li>
            ))}
          </ul>
        </CobaltSidePanel>
      </div>
    );
  }
);

InvoiceAlertPanel.displayName = 'InvoiceAlertPanel';
export { InvoiceAlertPanel };
